import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import concesionariaVentaProduccionService from '../../../services/concesionariaVentaProduccionService';
import { supplierService } from '../../../services/supplierService';
import { getProjects } from '../../../services/projectsService';
import Toast from '../../../components/Common/Toast';

const ROUTE_OPTIONS = [
  { value: 'none', label: 'Sin venta' },
  { value: 'service_order', label: 'OSV' },
  { value: 'cash_sale', label: 'Venta efectivo' },
];

const COLUMN_TAX_OPTIONS = [
  { value: 'yes', label: 'Incluye impuestos' },
  { value: 'no', label: 'No incluye impuestos' },
];

const LIST_PATH = '/produccion-concesionaria/configuraciones/cuadro-venta';

function formatPos(pos) {
  if (!pos) return '—';
  return `${pos.code ? `${pos.code} — ` : ''}${pos.name}`;
}

function formatServiceIgvLabel(priceIncludesIgv) {
  return priceIncludesIgv ? 'Incluido IGV' : 'Más IGV';
}

function formatServiceIgvHint(priceIncludesIgv) {
  return priceIncludesIgv ? 'Precio final al cliente' : 'Base antes de IGV';
}

const ConfigCuadroList = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState([]);
  const [posOptions, setPosOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPosId, setModalPosId] = useState('');
  const [modalName, setModalName] = useState('');
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, metaRes] = await Promise.all([
        concesionariaVentaProduccionService.getPlantillasCatalog(),
        concesionariaVentaProduccionService.getMeta(),
      ]);
      if (catRes.success) {
        setCatalog(catRes.data || []);
      }
      if (metaRes.success) {
        const list = metaRes.data?.point_of_sales || [];
        setPosOptions(list);
        if (list.length === 1) {
          setModalPosId(String(list[0].id));
        }
      }
    } catch {
      setToast({ isOpen: true, message: 'No se pudo cargar las plantillas', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const openCreateModal = () => {
    setModalName('');
    if (!modalPosId && posOptions.length === 1) {
      setModalPosId(String(posOptions[0].id));
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    if (creating) return;
    setModalOpen(false);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    const name = modalName.trim();
    if (!modalPosId || !name) {
      setToast({ isOpen: true, message: 'Indique POS y nombre de plantilla', type: 'error' });
      return;
    }
    setCreating(true);
    try {
      const res = await concesionariaVentaProduccionService.createPlantilla(modalPosId, { name });
      if (res.success && res.data?.id) {
        setModalOpen(false);
        navigate(`${LIST_PATH}/${res.data.id}`);
      }
    } catch (err) {
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'No se pudo crear la plantilla',
        type: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const modal = modalOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-label="Cerrar"
            onClick={closeModal}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-plantilla-cuadro-title"
            className="relative w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 id="modal-plantilla-cuadro-title" className="text-lg font-semibold text-gray-900">
                Nueva plantilla de cuadro
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Asocie la plantilla a un punto de venta; luego podrá definir sus columnas.
              </p>
            </div>
            <form onSubmit={submitCreate} className="px-5 py-4 space-y-4">
              <div>
                <label htmlFor="modal-plantilla-pos" className="block text-sm font-medium text-gray-700 mb-1">
                  Punto de venta
                </label>
                <select
                  id="modal-plantilla-pos"
                  value={modalPosId}
                  onChange={(e) => setModalPosId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
                >
                  <option value="">Seleccione…</option>
                  {posOptions.map((p) => (
                    <option key={p.id} value={String(p.id)}>
                      {formatPos(p)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="modal-plantilla-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de plantilla
                </label>
                <input
                  id="modal-plantilla-nombre"
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  placeholder="Ej. Turno mañana, Línea A…"
                  autoFocus
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={creating}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || !modalPosId || !modalName.trim()}
                  className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white text-sm font-medium"
                >
                  {creating ? 'Creando…' : 'Crear plantilla'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {modal}

      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">Producción concesionaria</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Cuadro venta — plantillas</span>
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Plantillas del cuadro de venta</h1>
          <p className="mt-1 text-sm text-gray-500">
            Cada plantilla define las columnas del cuadro para un punto de venta.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          disabled={posOptions.length === 0}
          className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          + Crear plantilla
        </button>
      </div>

      {loading ? (
        <p className="text-gray-600">Cargando plantillas…</p>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                <th className="px-4 py-3 font-medium">Punto de venta</th>
                <th className="px-4 py-3 font-medium">Plantilla</th>
                <th className="px-4 py-3 font-medium w-28">Columnas</th>
                <th className="px-4 py-3 font-medium w-24">Estado</th>
                <th className="px-4 py-3 font-medium w-32" />
              </tr>
            </thead>
            <tbody>
              {catalog.map((row) => (
                <tr key={row.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                  <td className="px-4 py-3 text-gray-800">{formatPos(row.point_of_sale)}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                  <td className="px-4 py-3 text-gray-600">{row.columns_count ?? 0}</td>
                  <td className="px-4 py-3">
                    {row.is_active ? (
                      <span className="text-teal-700 text-xs font-medium">Activa</span>
                    ) : (
                      <span className="text-gray-500 text-xs">Inactiva</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`${LIST_PATH}/${row.id}`}
                      className="text-teal-700 hover:text-teal-900 font-medium"
                    >
                      Configurar
                    </Link>
                  </td>
                </tr>
              ))}
              {catalog.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-gray-500">
                    No hay plantillas. Use &quot;Crear plantilla&quot; para agregar la primera.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

const ConfigCuadroDetail = ({ plantillaId }) => {
  const [plantilla, setPlantilla] = useState(null);
  const [columns, setColumns] = useState([]);
  const [comidaServices, setComidaServices] = useState([]);
  const [saleServicesForPos, setSaleServicesForPos] = useState([]);
  const [osvClientId, setOsvClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [clients, setClients] = useState([]);
  const [catalogProjects, setCatalogProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const posId = plantilla ? String(plantilla.point_of_sale_id) : '';

  const catalogProjectOptions = useMemo(
    () =>
      [...catalogProjects].sort((a, b) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'es'),
      ),
    [catalogProjects],
  );

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const catRes = await concesionariaVentaProduccionService.getPlantillasCatalog();
      const row = (catRes.data || []).find((p) => String(p.id) === String(plantillaId));
      if (!catRes.success || !row) {
        setPlantilla(null);
        return;
      }
      setPlantilla(row);
      setOsvClientId(row.osv_client_id ? String(row.osv_client_id) : '');
      setProjectId(row.project_id ? String(row.project_id) : '');
      const pos = row.point_of_sale_id;
      const colRes = await concesionariaVentaProduccionService.getColumnas(plantillaId);
      if (colRes.success) {
        setColumns(colRes.data?.length ? colRes.data : []);
      }
      try {
        const [comRes, ventaRes] = await Promise.all([
          concesionariaVentaProduccionService.getServiciosComida(pos),
          concesionariaVentaProduccionService.getServiciosVentaPos(pos),
        ]);
        if (ventaRes.success) {
          setSaleServicesForPos(ventaRes.data || []);
        } else {
          setSaleServicesForPos([]);
        }
        if (comRes.success) {
          setComidaServices(
            comRes.data?.length
              ? comRes.data.map((r) => ({ service_id: r.service_id, sort_order: r.sort_order }))
              : []
          );
        }
      } catch {
        setToast({
          isOpen: true,
          message: 'Columnas cargadas; no se pudieron cargar los servicios de comida del POS',
          type: 'warning',
        });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo cargar la plantilla',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [plantillaId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [clientRes, projRes] = await Promise.all([
          supplierService.getSuppliers({ entity_client: 1, active_only: 1 }),
          getProjects({ active_only: 1 }),
        ]);
        if (!cancelled && clientRes?.success && Array.isArray(clientRes.data)) {
          setClients(clientRes.data);
        }
        if (!cancelled && projRes?.success && Array.isArray(projRes.data)) {
          setCatalogProjects(projRes.data);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addColumn = () => {
    setColumns((prev) => [
      ...prev,
      {
        column_key: '',
        label: '',
        sort_order: prev.length,
        sale_route: 'none',
        has_igv: true,
        is_active: true,
      },
    ]);
  };

  const saveAll = async () => {
    if (!plantillaId || !posId) return;
    if (!osvClientId) {
      setToast({
        isOpen: true,
        message: 'Seleccione el cliente con el que saldrá la OSV al confirmar el cuadro.',
        type: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      await concesionariaVentaProduccionService.updatePlantilla(plantillaId, {
        osv_client_id: Number(osvClientId),
        project_id: projectId ? Number(projectId) : null,
      });
      await concesionariaVentaProduccionService.saveColumnas(plantillaId, columns);
      await concesionariaVentaProduccionService.saveServiciosComida(posId, comidaServices);
      setToast({ isOpen: true, message: 'Configuración guardada', type: 'success' });
      await loadDetail();
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo guardar',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const addComidaService = () => {
    setComidaServices((prev) => [...prev, { service_id: '', sort_order: prev.length }]);
  };

  const saleServiceById = useMemo(
    () => new Map(saleServicesForPos.map((s) => [Number(s.id), s])),
    [saleServicesForPos],
  );

  const saleServiceOptions = useMemo(() => {
    const byId = new Map(saleServicesForPos.map((s) => [s.id, s]));
    comidaServices.forEach((row) => {
      const id = row.service_id;
      if (id && !byId.has(id)) {
        byId.set(id, {
          id,
          sku: '',
          name: `(sin precio en POS) #${id}`,
          invalid: true,
        });
      }
    });
    return [...byId.values()].sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'));
  }, [saleServicesForPos, comidaServices]);

  if (loading) {
    return (
      <div className="p-6 max-w-6xl mx-auto text-gray-600">Cargando plantilla…</div>
    );
  }

  if (!plantilla) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <p className="text-gray-700 mb-4">Plantilla no encontrada o sin acceso.</p>
        <Link to={LIST_PATH} className="text-teal-700 hover:text-teal-900 font-medium">
          ← Volver al listado
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">Producción concesionaria</Link>
        <span className="mx-2">/</span>
        <Link to={LIST_PATH} className="hover:text-teal-600">Cuadro venta</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">{plantilla.name}</span>
      </nav>

      <Link to={LIST_PATH} className="inline-block text-sm text-teal-700 hover:text-teal-900 mb-3">
        ← Volver al listado
      </Link>

      <h1 className="text-2xl font-semibold text-gray-800">{plantilla.name}</h1>
      <p className="mt-1 text-sm text-gray-500">
        POS: <span className="text-gray-800">{formatPos(plantilla.point_of_sale)}</span>
        {' · '}
        Los servicios de comida se configuran por punto de venta (compartidos entre plantillas del mismo POS).
      </p>

      <section className="mt-8 border border-gray-200 rounded-lg bg-white p-5">
        <h2 className="text-lg font-semibold text-gray-900">Ventas al confirmar el cuadro</h2>
        <p className="mt-1 text-sm text-gray-500">
          La <strong>OSV</strong> (requerimiento) usará el cliente indicado aquí. Las columnas de{' '}
          <strong>venta en efectivo</strong> se emitirán a nombre de{' '}
          <span className="font-medium text-gray-800">CLIENTES VARIOS</span> (se crea automáticamente si no existe).
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="plantilla-osv-client" className="block text-sm font-medium text-gray-700 mb-1">
              Cliente OSV <span className="text-red-600">*</span>
            </label>
            <select
              id="plantilla-osv-client"
              value={osvClientId}
              onChange={(e) => setOsvClientId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Seleccione cliente…</option>
              {clients.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.code ? `${c.code} — ` : ''}{c.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="plantilla-project" className="block text-sm font-medium text-gray-700 mb-1">
              Proyecto / operación en líneas
            </label>
            <select
              id="plantilla-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
            >
              <option value="">Por defecto (destino o POS)</option>
              {catalogProjectOptions.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.code ? `${p.code} — ` : ''}{p.name}
                  {p.type ? ` (${p.type})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="mt-8">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Columnas de la plantilla</h2>
          <button
            type="button"
            onClick={addColumn}
            className="text-sm px-3 py-1.5 rounded-md bg-teal-600 text-white hover:bg-teal-700"
          >
            + Columna
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-500">
          La <strong>OSV</strong> al confirmar usa el <strong>requerimiento</strong> de cada fila (gravado según la primera
          columna OSV de esta plantilla). Las columnas <strong>OSV</strong> del cuadro solo sirven para control
          (diferencia/merma). Las de <strong>venta en efectivo</strong> generan <strong>boleta</strong> (con impuestos) u{' '}
          <strong>otro documento</strong> (sin impuestos).
        </p>
        <div className="mt-3 overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-3 py-2">Etiqueta</th>
                <th className="px-3 py-2">Ruta venta</th>
                <th className="px-3 py-2 w-48">Impuestos</th>
                <th className="px-3 py-2 w-16" />
              </tr>
            </thead>
            <tbody>
              {columns.map((col, idx) => (
                <tr key={idx} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      value={col.label}
                      onChange={(e) => {
                        const v = e.target.value;
                        setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, label: v } : c)));
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                      placeholder="Ej. Producido, Q con ticket…"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={col.sale_route}
                      onChange={(e) => {
                        const v = e.target.value;
                        setColumns((prev) => prev.map((c, i) => (i === idx ? { ...c, sale_route: v } : c)));
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1"
                    >
                      {ROUTE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={col.has_igv !== false ? 'yes' : 'no'}
                      disabled={col.sale_route === 'none'}
                      title={
                        col.sale_route === 'none'
                          ? 'Solo aplica a columnas con venta'
                          : col.has_igv !== false
                            ? 'Línea gravada con IGV'
                            : 'Línea sin IGV'
                      }
                      onChange={(e) => {
                        const hasIgv = e.target.value === 'yes';
                        setColumns((prev) =>
                          prev.map((c, i) => (i === idx ? { ...c, has_igv: hasIgv } : c)),
                        );
                      }}
                      className="w-full border border-gray-300 rounded px-2 py-1 disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      {COLUMN_TAX_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="text-red-600 text-xs"
                      onClick={() => setColumns((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      Quitar
                    </button>
                  </td>
                </tr>
              ))}
              {columns.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-gray-500 text-center">
                    Sin columnas. Agregue al menos una con ruta OSV o venta efectivo.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8 border-t border-gray-100 pt-8">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">Servicios de comida (por POS)</h2>
          <button
            type="button"
            onClick={addComidaService}
            disabled={saleServicesForPos.length === 0}
            className="text-sm px-3 py-1.5 rounded-md border border-teal-600 text-teal-700 hover:bg-teal-50 disabled:opacity-50"
          >
            + Servicio
          </button>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Solo aparecen servicios de venta con precio configurado en este punto de venta (Ventas → lista de precios de
          servicios). Abajo solo se muestra cómo está cargado el precio en lista (incluido / más IGV).
        </p>
        {saleServicesForPos.length === 0 && (
          <p className="mt-2 text-sm text-amber-800 p-2 rounded border border-amber-200 bg-amber-50">
            No hay servicios de venta con precio en este POS. Configure precios en Ventas antes de asignar el servicio
            del cuadro.
          </p>
        )}
        <div className="mt-3 overflow-x-auto border border-gray-200 rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700">
              <tr>
                <th className="px-3 py-2 font-medium">Servicio</th>
                <th className="px-3 py-2 font-medium w-28">Precio (S/)</th>
                <th className="px-3 py-2 font-medium w-36">IGV</th>
                <th className="px-3 py-2 font-medium w-16" />
              </tr>
            </thead>
            <tbody>
              {comidaServices.map((row, idx) => {
                const meta = row.service_id ? saleServiceById.get(Number(row.service_id)) : null;
                const includesIgv = meta?.price_includes_igv === true;
                return (
                  <tr key={idx} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <select
                        value={row.service_id}
                        onChange={(e) => {
                          const v = e.target.value;
                          setComidaServices((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, service_id: v ? Number(v) : '' } : r))
                          );
                        }}
                        className="w-full min-w-[200px] border border-gray-300 rounded px-2 py-1.5 text-sm"
                      >
                        <option value="">Servicio…</option>
                        {saleServiceOptions.map((s) => (
                          <option key={s.id} value={s.id} disabled={s.invalid}>
                            {s.sku ? `${s.sku} — ` : ''}{s.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {meta?.pos_price != null ? Number(meta.pos_price).toFixed(2) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {meta ? (
                        <div>
                          <span className="text-gray-900">{formatServiceIgvLabel(includesIgv)}</span>
                          <p className="text-[11px] text-gray-500 mt-0.5">{formatServiceIgvHint(includesIgv)}</p>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="text-red-600 text-xs"
                        onClick={() => setComidaServices((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                );
              })}
              {comidaServices.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-500">
                    Sin servicios asignados. Use &quot;+ Servicio&quot; si el cuadro usa comida con precio de lista.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="mt-8">
        <button
          type="button"
          disabled={saving}
          onClick={saveAll}
          className="px-5 py-2 rounded-md bg-teal-600 text-white font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {saving ? 'Guardando…' : 'Guardar configuración'}
        </button>
      </div>

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

const ConfigCuadro = () => {
  const { plantillaId } = useParams();
  if (plantillaId) {
    return <ConfigCuadroDetail plantillaId={plantillaId} />;
  }
  return <ConfigCuadroList />;
};

export default ConfigCuadro;
